import { TIMEZONE_MAP } from '@/common/constants';
import { newE2EPage } from '@stencil/core/testing';

describe('nylas-availability-picker', () => {
  beforeEach(() => {
    // Backup the original Intl.DateTimeFormat
    const RealDateTimeFormat = Intl.DateTimeFormat;

    // Mock Intl.DateTimeFormat
    jest.spyOn(Intl, 'DateTimeFormat').mockImplementation((...args) => {
      const instance = new RealDateTimeFormat(...args);
      return {
        ...instance,
        resolvedOptions: () => ({
          ...instance.resolvedOptions(),
          timeZone: 'America/Toronto', // Mocked timezone
        }),
      };
    });
  });

  afterEach(() => {
    // Restore the original Intl.DateTimeFormat after each test
    jest.restoreAllMocks();
  });

  it('renders', async () => {
    const page = await newE2EPage();
    await page.setContent(
      `<form id="availability-picker">
        <nylas-availability-picker name="availability"></nylas-availability-picker>
      </form>`,
    );

    const screenshot = await page.compareScreenshot();
    expect(screenshot).toMatchScreenshot();
  });

  it('renders correctly with default props', async () => {
    const page = await newE2EPage();
    await page.setContent(
      `<form id="availability-picker">
        <nylas-availability-picker name="availability"></nylas-availability-picker>
      </form>`,
    );
    const element = await page.find('nylas-availability-picker');
    expect(element).toHaveClass('hydrated');
  });

  it('renders correctly with availability days', async () => {
    const page = await newE2EPage();

    // Inject initial openHours as a prop to the component
    await page.setContent(`<form id="availability-picker">
      <nylas-availability-picker name="availability"></nylas-availability-picker>
    </form>`);

    const element = await page.find('nylas-availability-picker');
    expect(element).toHaveClass('hydrated');

    // Check if the component properly processed and rendered the openHours
    const availabilityDays = await page.findAll('nylas-availability-picker >>> .availability-day');
    expect(availabilityDays.length).toBeGreaterThan(0); // Expecting at least one day to be rendered
  });

  it('correctly marks days from the default state', async () => {
    const page = await newE2EPage();

    await page.setContent(`<nylas-availability-picker></nylas-availability-picker>`);

    await page.waitForChanges();

    const daysMarked = await page.findAll('nylas-availability-picker >>> .availability-day input[type="checkbox"]:checked');
    expect(daysMarked.length).toEqual(5); // Expecting 5 days to be marked as checked
    const allDays = await page.findAll('nylas-availability-picker >>> .availability-day input[type="checkbox"]');
    expect(allDays.length).toEqual(7); // Expecting 7 days to be rendered

    expect(await allDays[0].getProperty('checked')).toEqual(false); // Expecting Sunday to be unchecked
    expect(await allDays[1].getProperty('checked')).toEqual(true); // Expecting Monday to be checked
    expect(await allDays[2].getProperty('checked')).toEqual(true); // Expecting Tuesday to be checked
    expect(await allDays[3].getProperty('checked')).toEqual(true); // Expecting Wednesday to be checked
    expect(await allDays[4].getProperty('checked')).toEqual(true); // Expecting Thursday to be checked
    expect(await allDays[5].getProperty('checked')).toEqual(true); // Expecting Friday to be checked
    expect(await allDays[6].getProperty('checked')).toEqual(false); // Expecting Saturday to be unchecked
  });

  it('displays correct time ranges', async () => {
    const page = await newE2EPage();

    // Inject initial openHours as a prop to the component
    await page.setContent(`
      <nylas-availability-picker></nylas-availability-picker>
    `);

    // Find the time range display elements for one of the days, for example, Monday (1)
    const days = await page.findAll('nylas-availability-picker >>> .availability-day');
    const monday = days[1];
    const timeRangeStart = await monday.find('.time-ranges .time-range nylas-time-window-picker[id="MON:0_start"] >>> .time-picker input');
    expect(timeRangeStart).not.toBeNull();
    expect(await timeRangeStart.getProperty('value')).toEqual('09:00am');
    const timeRangeEnd = await monday.find('.time-ranges .time-range nylas-time-window-picker[id="MON:0_end"] >>> .time-picker input');
    expect(timeRangeEnd).not.toBeNull();
    expect(await timeRangeEnd.getProperty('value')).toEqual('05:00pm');
  });

  it('displays correct timezone and selects timezone', async () => {
    const page = await newE2EPage();

    await page.evaluateOnNewDocument(() => {
      const originalDateTimeFormat = Intl.DateTimeFormat;
      (Intl as any).DateTimeFormat = function (...args: any[]) {
        const instance = new originalDateTimeFormat(...args);
        return {
          ...instance,
          resolvedOptions: function () {
            return {
              ...instance.resolvedOptions(),
              timeZone: 'America/Toronto',
            };
          },
          supportedLocalesOf: function (_locales: string | string[], _options?: Intl.DateTimeFormatOptions): string[] {
            return [];
          },
        };
      };
    });

    await page.setContent(`
      <nylas-availability-picker></nylas-availability-picker>
    `);
    await page.waitForChanges();

    const defaultTimezone = TIMEZONE_MAP[Intl.DateTimeFormat().resolvedOptions().timeZone];
    // Find the timezone display element
    const selectTimezone = await page.find('nylas-availability-picker >>> .select-timezone');
    expect(selectTimezone).not.toBeNull();
    const timezone = await selectTimezone.find('select-dropdown >>> .dropdown button .selected-option');
    expect(timezone).not.toBeNull();

    expect(await timezone.innerText).toEqual(defaultTimezone);
    await timezone.click();
    await page.waitForChanges();
    await page.keyboard.press('ArrowDown');
    const timezoneInput = await selectTimezone.find('select-dropdown >>> .dropdown-content input[type="text"]');
    expect(timezoneInput).not.toBeNull();
    await timezoneInput.type('Vancouver');
    await page.waitForChanges();
    const li = await selectTimezone.find('select-dropdown >>> .dropdown-content ul li');
    await li.click();
    await page.waitForChanges();
    expect(await timezone.innerText).toEqual('America / Vancouver');
  });

  it('selects a day successfully', async () => {
    const page = await newE2EPage();

    await page.setContent(`
      <nylas-availability-picker></nylas-availability-picker>
    `);

    const days = await page.findAll('nylas-availability-picker >>> .availability-day input[type="checkbox"]');
    const sunday = days[0];
    await sunday.click();
    await page.waitForChanges();
    const newSelectedDays = await page.findAll('nylas-availability-picker >>> .availability-day input[type="checkbox"]:checked');
    expect(newSelectedDays.length).toEqual(6);
  });

  it('updates the time range successfully', async () => {
    const page = await newE2EPage();

    await page.setContent(`
      <nylas-availability-picker></nylas-availability-picker>
    `);

    const days = await page.findAll('nylas-availability-picker >>> .availability-day');
    const monday = days[1];
    const timeRangeStart = await monday.find('.time-ranges .time-range nylas-time-window-picker[id="MON:0_start"] >>> .time-picker input');
    expect(timeRangeStart).not.toBeNull();
    await timeRangeStart.click();
    // Check that input value is focused in the dropdown list
    const focusedOption = await monday.find('.time-ranges .time-range nylas-time-window-picker[id="MON:0_start"] >>> .time-picker .times ul li.focused');
    expect(focusedOption).not.toBeNull();
    expect(await focusedOption.innerText).toEqual('09:00am');

    // Pressing the down arrow key to select the next time slot
    await timeRangeStart.press('ArrowDown');
    await page.keyboard.press('Enter');
    await page.waitForChanges();
    expect(await timeRangeStart.getProperty('value')).toEqual('09:15am');

    // Manually typing a time range
    await timeRangeStart.focus();
    await timeRangeStart.press('ArrowDown');
    // Pressing the down arrow key 8 times to clear the input
    await timeRangeStart.press('Backspace');
    await timeRangeStart.press('Backspace');
    await timeRangeStart.press('Backspace');
    await timeRangeStart.press('Backspace');
    await timeRangeStart.press('Backspace');
    await timeRangeStart.press('Backspace');
    await timeRangeStart.press('Backspace');
    await timeRangeStart.type('01:00pm');
    await page.waitForChanges();

    await timeRangeStart.press('ArrowDown');
    await page.keyboard.press('Enter');
    await page.waitForChanges();
    expect(await timeRangeStart.getProperty('value')).toEqual('01:15pm');
  });
});
